import type { JobModel, JobResponse, JobResponseData } from '@/types/jobModel';
import constants from '@/utils/constants';

const RETAIL_HOURLY_WAGE_CAREER_LEVEL_IDS = [
  constants.JOBS.CAREER_LEVEL_ID.STUDENT_ASSISTANT,
  constants.JOBS.CAREER_LEVEL_ID.EXPERIENCED_PROFESSIONAL,
  constants.JOBS.CAREER_LEVEL_ID.INITIATIVE,
  constants.JOBS.CAREER_LEVEL_ID.RETAIL_STAFF,
  constants.JOBS.CAREER_LEVEL_ID.YOUNG_PROFESSIONAL,
];

const APPRENTICESHIP_DEPARTMENT_IDS = [
  constants.JOBS.DEPARTMENT_ID.APPRENTICESHIP_HEADQUARTER,
  constants.JOBS.DEPARTMENT_ID.APPRENTICESHIP_RETAIL,
];

const CONTRACT_LENGTH_EXCLUDED_SCOPE_IDS = [
  constants.JOBS.SCOPE_ID.TEMPORARY_STAFF,
  constants.JOBS.SCOPE_ID.WORKING_STUDENT,
];

const CONTRACT_LENGTH_EXCLUDED_CAREER_LEVEL_IDS = [
  constants.JOBS.CAREER_LEVEL_ID.WORKING_STUDENT,
  constants.JOBS.CAREER_LEVEL_ID.INITIATIVE,
  constants.JOBS.CAREER_LEVEL_ID.APPRENTICESHIP,
];

export function jobModel(data: JobResponse | null) {
  let whoWeAre: string;
  let description1: string;
  let description2: string;
  let fullAddress: string;

  return {
    getId(): string {
      return data?.id ?? '';
    },

    getTitle(): string {
      return data?.title ?? '';
    },

    getLanguage(): string {
      return data?.language ?? '';
    },

    getCareerLevelId(): string {
      return String(data?.careerLevelId);
    },

    getCareerLevel(): string {
      return data?.careerLevel ?? '';
    },

    getDepartmentId(): string {
      return String(data?.departmentId);
    },

    getDepartment(): string {
      return data?.department ?? '';
    },

    getScopeId(): string {
      return String(data?.scopeId);
    },

    getSeoScope(): string | string[] {
      const scopeMapping: { [key: string]: string | string[] } = {
        7365: ['FULL_TIME', 'PART_TIME'],
        4465: 'FULL_TIME',
        4466: 'PART_TIME',
        4532: 'TEMPORARY',
        8891: 'INTERN',
      };
      return scopeMapping[data?.scopeId ?? 0] || 'OTHER';
    },

    getScope(): string {
      return data?.scope ?? '';
    },

    getContinentId(): string {
      return String(data?.continentId);
    },

    getContinent(): string {
      return data?.continent ?? '';
    },

    getCountryId(): string {
      return String(data?.countryId);
    },

    getCountry(): string {
      return data?.country ?? '';
    },

    getRegionId(): string {
      return String(data?.regionId);
    },

    getRegion(): string {
      return data?.region ?? '';
    },

    getLocation(): string {
      return data?.location ?? '';
    },

    getZipCode(): string {
      return data?.zipCode ?? '';
    },

    getFullAddress(): string {
      if (!fullAddress) {
        const streetAddress = data?.streetAddress ?? '';
        const zipCodeAndCity = `${this.getZipCode()} ${this.getLocation()}`.trim();
        fullAddress = [streetAddress, zipCodeAndCity].filter(Boolean).join(', ');
      }
      return fullAddress;
    },

    getDivisionId(): string {
      return String(data?.divisionId);
    },

    getDivision(): string {
      return data?.division ?? '';
    },

    getWhoWeAre(): string {
      whoWeAre = whoWeAre || removeEmptyHtmlParagraphs(data?.whoWeAre ?? '');
      return whoWeAre;
    },

    getDescription1(): string {
      description1 = description1 || removeEmptyHtmlParagraphs(data?.desc1 ?? '');
      return description1;
    },

    getDescription2(): string {
      description2 = description2 || removeEmptyHtmlParagraphs(data?.desc2 ?? '');
      return description2;
    },

    getHrName(): string {
      return data?.hrName ?? '';
    },

    getHrMail(): string {
      return data?.hrMail ?? '';
    },

    getPublishedDate(): number {
      return data?.published ?? 0;
    },

    getUrl(): string {
      return `${this.getTitle()
        .replace(/\W/g, ' ')
        .replace(/\s{1,5}/g, '-')
        .toLocaleLowerCase()}-${this.getId()}`;
    },

    getFutureLocaleStartDate(): string {
      const startDate: Date = this.getStartDate();
      const isInTheFuture: boolean = startDate > new Date();
      return isInTheFuture ? this.getLocaleStartDate() : '';
    },

    getStartDate(): Date {
      return new Date(data?.startDate ?? '');
    },

    getLocaleStartDate(): string {
      return this.getStartDate().toLocaleDateString('de', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    isFranchiseDepartment(): boolean {
      return this.getDepartmentId() === constants.JOBS.DEPARTMENT_ID.FRANCHISE;
    },

    isConcessionDepartment(): boolean {
      return this.getDepartmentId() === constants.JOBS.DEPARTMENT_ID.CONCESSION;
    },

    isApprenticeship(): boolean {
      /**
       * Apprenticeship departments (prod)
       * 10157 - DE: Ausbildung HQ      | EN: Apprenticeship HQ
       * 11523 - DE: Ausbildung Retail  | EN: Apprenticeship Retail
       *
       * Apprenticeship career level (prod)
       * 5496  - DE: Ausbildung         | EN: Apprenticeship
       */
      return (
        APPRENTICESHIP_DEPARTMENT_IDS.includes(this.getDepartmentId()) ||
        this.getCareerLevelId() === constants.JOBS.CAREER_LEVEL_ID.APPRENTICESHIP
      );
    },

    isInternship(): boolean {
      /**
       * Internship scope (prod)
       * 8891  - DE: Praktikum          | EN: Internship
       *
       * Internship career level (prod)
       * 5501  - DE: Praktikum          | EN: Internship
       */
      return (
        this.getScopeId() === constants.JOBS.SCOPE_ID.INTERNSHIP ||
        this.getCareerLevelId() === constants.JOBS.CAREER_LEVEL_ID.INTERNSHIP
      );
    },

    isRetail(): boolean {
      /**
       * Retail division (prod)
       * 9917  - DE: Retail             | EN: Retail
       */
      return this.getDivisionId() === constants.JOBS.DIVISION_ID.RETAIL;
    },

    isHourlyWage(): boolean {
      const isRetailHourlyWage =
        this.isRetail() && RETAIL_HOURLY_WAGE_CAREER_LEVEL_IDS.includes(this.getCareerLevelId());
      return this.isFreelancerJob() || isRetailHourlyWage;
    },

    hasNoticePeriod(): boolean {
      return !(this.isFreelancerJob() || this.isInternship() || this.isApprenticeship());
    },

    hasSalaryExpectations(): boolean {
      if (this.getCountryId() === constants.JOBS.COUNTRY_ID.POLAND) {
        return false;
      }
      return !(this.isInternship() || this.isApprenticeship());
    },

    getContractLength(): ['permanent' | 'fixed_term', string | null] | null {
      /**
       * Excluded from consideration
       *
       * Production
       * Career
       * 9178 - working student
       * 5499 - initiative
       * 5496 - apprenticeship
       *
       * Scope
       * 4532 - temporary staff
       * 4533 - student temp staff
       */
      if (
        CONTRACT_LENGTH_EXCLUDED_SCOPE_IDS.includes(this.getScopeId()) ||
        CONTRACT_LENGTH_EXCLUDED_CAREER_LEVEL_IDS.includes(this.getCareerLevelId())
      ) {
        return null;
      }
      return data?.terminationDate
        ? [
            'fixed_term',
            new Date(data?.terminationDate).toLocaleDateString('de', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
          ]
        : ['permanent', null];
    },

    isFreelancerJob(): boolean {
      return this.getScopeId() === constants.JOBS.SCOPE_ID.FREELANCER;
    },

    containsSearchTerm(searchTerm: string): boolean {
      const title: string = data?.title?.toLocaleLowerCase() ?? '';
      const department: string = data?.department?.toLocaleLowerCase() ?? '';
      const careerLevel: string = data?.careerLevel?.toLocaleLowerCase() ?? '';
      const city: string = data?.location?.toLocaleLowerCase() ?? '';
      const country: string = data?.country?.toLocaleLowerCase() ?? '';
      const division: string = data?.division?.toLocaleLowerCase() ?? '';
      const deployment: string = data?.scope?.toLocaleLowerCase() ?? '';
      if (!title && !careerLevel && !department && !city && !country && !division && !deployment) {
        return false;
      }
      const searchTerms: string[] = searchTerm.toLocaleLowerCase().split(' ');
      return searchTerms.every(
        (term) =>
          title?.includes(term) ||
          department?.includes(term) ||
          careerLevel?.includes(term) ||
          city?.includes(term) ||
          country?.includes(term) ||
          division?.includes(term) ||
          deployment?.includes(term),
      );
    },

    containsFilterValue(filterName: string, filterValue: string | string[]): boolean {
      let value = '';
      if (filterName === 'department') {
        value = this.getDepartmentId();
      } else if (filterName === 'continent') {
        value = this.getContinentId();
      } else if (filterName === 'country') {
        value = this.getCountryId();
      } else if (filterName === 'region') {
        value = this.getRegionId();
      } else if (filterName === 'division') {
        value = this.getDivisionId();
      } else if (filterName === 'career') {
        value = this.getCareerLevelId();
      } else if (filterName === 'scope') {
        value = this.getScopeId();
      }
      if (!value) {
        return false;
      }
      if (Array.isArray(filterValue)) {
        return filterValue.includes(value);
      }
      return value === filterValue;
    },

    getGtmTrackData() {
      return {
        item_id: this.getId(),
        item_name: this.getTitle(),
        start_date: this.getFutureLocaleStartDate(),
        scope: this.getScope(),
        location: this.getRegion(),
        country: this.getCountry(),
        department: this.getDepartment(),
      };
    },

    getData() {
      return data;
    },
  };
}

export function jobListModel(responseData: JobResponseData) {
  return {
    getList(): JobModel[] {
      return responseData?.data?.map((item: any) => jobModel(item)) ?? [];
    },
  };
}
